import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private logLevel: LogLevel = environment.production ? LogLevel.WARN : LogLevel.DEBUG;
  private enableConsole = true;

  /**
   * Configura el nivel de logging
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  /**
   * Habilita o deshabilita el logging en consola
   */
  setConsoleLogging(enabled: boolean): void {
    this.enableConsole = enabled;
  }

  /**
   * Log de nivel DEBUG - información detallada para desarrollo
   */
  debug(message: string, ...optionalParams: unknown[]): void {
    this.log(LogLevel.DEBUG, message, optionalParams);
  }

  /**
   * Log de nivel INFO - información general
   */
  info(message: string, ...optionalParams: unknown[]): void {
    this.log(LogLevel.INFO, message, optionalParams);
  }

  /**
   * Log de nivel WARN - advertencias
   */
  warn(message: string, ...optionalParams: unknown[]): void {
    this.log(LogLevel.WARN, message, optionalParams);
  }

  /**
   * Log de nivel ERROR - errores
   */
  error(message: string, error?: Error | unknown, ...optionalParams: unknown[]): void {
    const params = error ? [error, ...optionalParams] : optionalParams;
    this.log(LogLevel.ERROR, message, params);
  }

  /**
   * Método privado para realizar el logging
   */
  private log(level: LogLevel, message: string, params: unknown[]): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${LogLevel[level]}]`;
    const fullMessage = `${prefix} ${message}`;

    if (this.enableConsole) {
      switch (level) {
        case LogLevel.DEBUG:
        case LogLevel.INFO:
          // eslint-disable-next-line no-console
          console.log(fullMessage, ...params);
          break;
        case LogLevel.WARN:
          console.warn(fullMessage, ...params);
          break;
        case LogLevel.ERROR:
          console.error(fullMessage, ...params);
          break;
      }
    }

    // Aquí se puede integrar con servicios externos de logging
    // como Sentry, LogRocket, etc.
    // this.sendToExternalService(level, fullMessage, params);
  }

  /**
   * Verifica si se debe realizar el log según el nivel configurado
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }
}
